export interface FaqItem {
  question: string
  answer: string
}

export interface FaqCategory {
  id: string
  title: string
  items: FaqItem[]
}

export const faqCategories: FaqCategory[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    items: [
      {
        question: 'How do I log in to Paver?',
        answer:
          'Navigate to the login page and enter the email and password provided by your company admin. If you received an invite email, click the link to set up your account first.',
      },
      {
        question: 'What are the different user roles?',
        answer:
          'Paver has four roles. Estimators create and manage their own estimates and proposals. Managers can see all company data and approve or reject estimates. Admins have full access including pricing, company settings, and user management. Superadmins are Paver platform staff.',
      },
      {
        question: 'What can I see based on my role?',
        answer:
          'Estimators see only their own estimates, proposals, reports, and dashboard stats. Managers and Admins see data across all users in the company. Pricing and Company Settings are only visible to Admins.',
      },
      {
        question: 'How do I update my profile?',
        answer:
          'Click your avatar in the top-right corner of the header and select "Profile" from the dropdown menu. From there you can update your name, email, phone number, and password.',
      },
      {
        question: 'How do I navigate the app?',
        answer:
          'Use the sidebar on the left to access all major sections: Dashboard, Estimates, Proposals, Reports, and RFP Requests. On smaller screens, tap the menu icon in the header to open the sidebar.',
      },
      {
        question: 'Can I use Paver on my phone or tablet?',
        answer:
          'Yes. Paver is fully responsive and works on phones, tablets, and desktops. The sidebar collapses automatically on smaller screens and can be opened via the menu icon.',
      },
    ],
  },
  {
    id: 'estimates',
    title: 'Estimates',
    items: [
      {
        question: 'How do I create a new estimate?',
        answer:
          'Go to the Estimates page and click "New Estimate." A 4-step wizard will guide you through selecting public works status, project types (Asphalt, Concrete, Grading, Seal Coat, Striping), choosing or creating a client, and entering project details like job address and supervisor info.',
      },
      {
        question: 'What are project types and how do they affect the estimate?',
        answer:
          'Project types determine which tabs appear in the estimate builder. For example, selecting "Seal Coat" adds a Seal Coat tab with relevant sections for equipment, labor, materials, and miscellaneous costs. You can select multiple project types during the wizard.',
      },
      {
        question: 'How do I add line items to an estimate?',
        answer:
          'Inside the estimate builder, each section (Asphalt, Seal Coat, Striping, etc.) has Equipment, Labor, Materials, and Misc sub-sections. Click the "Add" dropdown in any sub-section to pick items from your pricing catalog or create a custom entry. You can edit quantities and rates inline.',
      },
      {
        question: 'How does markup work?',
        answer:
          'The sticky footer at the bottom of the estimate builder shows your subtotal. Enter a markup percentage and the dollar amount is calculated automatically. The bid total equals the subtotal plus markup. Changes update in real time.',
      },
      {
        question: 'What do the estimate statuses mean?',
        answer:
          'Draft means you are still working on it. Submitted for Approval means it has been sent to a manager for review. Approved means a manager approved it. Rejected means a manager sent it back with notes — when you open a rejected estimate, it automatically reverts to Draft so you can make changes.',
      },
      {
        question: 'How do I submit an estimate for approval?',
        answer:
          'In the estimate builder, scroll to the Bid Totals section at the bottom. Click the "Submit for Approval" button. The estimate status will change and your manager will see it in their "Waiting for Approval" tab.',
      },
      {
        question: 'How do managers approve or reject estimates?',
        answer:
          'Managers see a "Waiting for Approval" tab on the Estimates list page with a red badge showing the count. Open any pending estimate, review the details, and use the Approve or Reject buttons. Rejected estimates require a note explaining the reason.',
      },
      {
        question: 'What is unit pricing?',
        answer:
          'Unit pricing is a toggle available in the project details step of the wizard. When enabled, pricing is calculated on a per-unit basis rather than lump sum. This is common for public works and government contracts.',
      },
      {
        question: 'What does the Public Works toggle do?',
        answer:
          'When an estimate is marked as Public Works, labor items added from the pricing catalog will use the Public Works Rate instead of the standard rate. This only affects new items added from the catalog — existing line items keep their saved rate.',
      },
      {
        question: 'Can I reorder line items?',
        answer:
          'Yes. Drag and drop line items within their section to reorder them. The original row dims while a ghost copy follows your cursor, and items swap positions as you drag past them.',
      },
    ],
  },
  {
    id: 'proposals',
    title: 'Proposals',
    items: [
      {
        question: 'How do I create a proposal from an estimate?',
        answer:
          'Once an estimate is approved, navigate to the estimate and use the "Generate Proposal" action. This creates a proposal record with a unique number (PROP-YYYY-NNN) linked to that estimate.',
      },
      {
        question: 'How do I send a proposal to a client?',
        answer:
          'From the Proposals list, click the "Send" button on any draft proposal. Enter one or more client email addresses in the send dialog and confirm. The client will receive a branded email with a link to view and sign the proposal.',
      },
      {
        question: 'Can I resend a proposal?',
        answer:
          'Yes. Proposals that have already been sent show a "Resend" button. This sends a fresh email to the same recipients or new ones you add.',
      },
      {
        question: 'What are the proposal statuses?',
        answer:
          'Draft means the proposal has not been sent yet. Sent means the email was delivered. Viewed means the client opened the proposal link. Signed means the client completed the digital signature. Expired appears automatically if the expiration date has passed without a signature.',
      },
      {
        question: 'How do I download a proposal as a PDF?',
        answer:
          'Open the proposal preview and click the "Download PDF" button. The PDF is generated on the server with proper page breaks, your company branding, cover photo, and all proposal content including maps and asset images.',
      },
      {
        question: 'How does the digital signature work?',
        answer:
          'When a client opens the proposal link from their email, they see the full proposal with a signature block at the bottom. They can draw their signature with a mouse or finger, then submit. The signed date and signature are captured and stored.',
      },
      {
        question: 'What is the cover letter?',
        answer:
          'The cover letter is an optional page that appears between the cover page and the proposal body. Admins can write and enable it under Company Settings → Proposal Content. It includes your company branding and the estimator\'s contact information.',
      },
      {
        question: 'How do I change the proposal cover photo?',
        answer:
          'Go to Company Settings → Proposal Content → Cover Photo. Choose from four professional paving industry images. The selected photo appears on all proposal covers with your brand color overlay.',
      },
    ],
  },
  {
    id: 'mapping-assets',
    title: 'Mapping & Assets',
    items: [
      {
        question: 'How do I use the map in an estimate?',
        answer:
          'Open an estimate and click the Map tab. Use the address search bar to navigate to the job site. The map shows a satellite view where you can draw shapes to measure areas and distances.',
      },
      {
        question: 'What drawing tools are available on the map?',
        answer:
          'The map toolbar includes three drawing tools: Polygon for measuring areas, Line for measuring distances, and Freehand for drawing irregular shapes. Each shape can be assigned a color from the palette.',
      },
      {
        question: 'How do measurements work?',
        answer:
          'After drawing a shape, the measurement panel shows the calculated area or length. You can switch units — square feet, square yards, or acres for areas, and linear feet or feet for lines.',
      },
      {
        question: 'Can I export the map?',
        answer:
          'Yes. Click the screenshot/export button to save the current map view as a PNG image. This can be included in your proposal by saving it as a map asset.',
      },
      {
        question: 'How do I upload photos and assets?',
        answer:
          'Go to the Assets tab in the estimate builder. Drag and drop images or click to browse. Supported formats are JPG, PNG, and WEBP up to 10 MB. Images are automatically compressed for optimal storage.',
      },
      {
        question: 'What are asset categories?',
        answer:
          'Each uploaded asset can be categorized as Map, Attachments, Asphalt, Concrete, Grading, Seal Coat, or Striping. The category determines where the image appears in the generated proposal — in its respective section, or at the end as an attachment.',
      },
    ],
  },
  {
    id: 'reports',
    title: 'Reports',
    items: [
      {
        question: 'What reports are available?',
        answer:
          'Paver includes 10 reports in three categories. Sales: Win/Loss, Pipeline, Revenue by Type, and Average Deal Size. Performance: Estimator Comparison, Markup Analysis, and Rejection Rate. Efficiency: Cycle Time, Approval Queue, and Response Time.',
      },
      {
        question: 'How do I filter report data?',
        answer:
          'Each report has a filters sidebar with date range presets (30 days, 90 days, quarter, year-to-date, last year), project type checkboxes, client filter, and a public works toggle. Managers and admins also get an estimator dropdown to filter by team member.',
      },
      {
        question: 'Can I export report data?',
        answer:
          'Yes. Each report includes a CSV export button that downloads the underlying data as a spreadsheet-compatible file you can open in Excel or Google Sheets.',
      },
      {
        question: 'Why do I only see my own data in reports?',
        answer:
          'Estimators only see reports based on their own estimates and proposals. This is by design for data privacy. Managers and admins see reports across all company data.',
      },
    ],
  },
  {
    id: 'rfp-requests',
    title: 'RFP Requests',
    items: [
      {
        question: 'What is an RFP Request?',
        answer:
          'RFP (Request for Proposal) is an intake record for capturing inbound project inquiries. It stores the contact information, project address, and type of work requested. Each RFP gets an auto-generated number like RFP-2026-001.',
      },
      {
        question: 'Who can create RFP Requests?',
        answer:
          'Managers and admins can create new RFP Requests. Estimators can view the list and details of all RFPs assigned to them.',
      },
      {
        question: 'How do I start an estimate from an RFP?',
        answer:
          'Open an RFP detail page and click the "Start Estimate" button. This opens the new estimate wizard with the client name and project address pre-filled from the RFP record, saving you time on data entry.',
      },
      {
        question: 'What work types can I select for an RFP?',
        answer:
          'Available work types include New Paving, Removal/Replacement, Concrete, Sealcoating, Striping, Overlays, Patching, ADA Compliance, Pressure Washing, Graffiti Removal, and Other or Unsure.',
      },
      {
        question: 'Does the RFP address support autocomplete?',
        answer:
          'Yes. The street address field uses Mapbox address autocomplete. Start typing and suggestions will appear. Selecting a suggestion auto-fills the city, state, and zip code.',
      },
    ],
  },
  {
    id: 'pricing-crews',
    title: 'Pricing & Crews',
    items: [
      {
        question: 'Who can manage pricing?',
        answer:
          'Only Admins can access the Pricing section. It is hidden from the sidebar for Estimators and Managers. Pricing items set here populate the catalog that all estimators use when adding line items.',
      },
      {
        question: 'What pricing categories exist?',
        answer:
          'There are five pricing categories: Equipment (daily rates), Laborers (hourly rates), Materials (per-unit costs), Miscellaneous Costs, and Striping Works. Each has its own page under the Pricing section.',
      },
      {
        question: 'How do I add a new pricing item?',
        answer:
          'Navigate to the appropriate pricing category page and click "Add." Fill in the item name, rate, unit of measure, and optionally a public works rate. The item will appear in the catalog for all estimators.',
      },
      {
        question: 'Can I import pricing items in bulk?',
        answer:
          'Yes. Each pricing category supports bulk import. Prepare your data and use the import function to upsert items by name and category. Existing items with matching names are updated; new items are created.',
      },
      {
        question: 'What are crews?',
        answer:
          'Crews are pre-defined groups of labor and equipment items. For example, a "Paving Crew A" might include a Foreman, Operators, and Laborers. In the estimate builder, selecting a crew lets you add all its members as line items at once.',
      },
      {
        question: 'What are Inclusions and Exclusions?',
        answer:
          'These are standard text clauses that appear on proposals. Inclusions describe what is covered in the price (e.g., "All labor and materials"). Exclusions clarify what is not included (e.g., "Utility relocation"). Admins manage these under Pricing → Inclusions/Exclusions.',
      },
    ],
  },
  {
    id: 'settings',
    title: 'Company Settings',
    items: [
      {
        question: 'Who can access Company Settings?',
        answer:
          'Only Admins have access to Company Settings and User Management. These sections are hidden from the sidebar for other roles.',
      },
      {
        question: 'How do I update company branding?',
        answer:
          'Go to Company Settings → Brand tab. Upload your company logo, set your primary brand color, and update your company name. These settings are applied across proposals, emails, and the PDF cover page.',
      },
      {
        question: 'How do I manage proposal templates?',
        answer:
          'Under Company Settings → Templates, you can choose from five cover templates (Classic, Modern, Bold, Technical, Compact). Each applies a different layout to your proposal cover page while using your brand colors and cover photo.',
      },
      {
        question: 'How do I manage users?',
        answer:
          'Go to User Management in the Admin section. You can invite new users by email, assign roles (Estimator, Manager, Admin), and deactivate accounts. Invited users receive an email link to set up their password.',
      },
      {
        question: 'How do I set up the cover letter?',
        answer:
          'Navigate to Company Settings → Proposal Content. Write your cover letter text in the provided area and check "Include cover letter in proposals." The letter will appear between the cover page and the proposal body, with the estimator\'s contact info included automatically.',
      },
    ],
  },
]
